import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

/**
 * Manages push notification device registration with the backend.
 * Does NOT own OneSignal SDK logic – only calls REST endpoints.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private http = inject(HttpClient);

  private playerId = signal<string | null>(null);
  ready = signal<boolean>(false);

  // Called once after OneSignal delivers a player ID
  setPlayerId(id: string) {
    this.playerId.set(id);
    this.ready.set(true);
  }

  /**
   * Register the current device for push notifications.
   * Safe to call even if already registered – backend is idempotent.
   * No longer checks authentication – called only by AuthService when user is logged in.
   */
  async registerDevice(platform: string = 'web') {
    const id = this.playerId();
    if (!id) return;
    try {
      await lastValueFrom(
        this.http.post('/auth/register-device', { player_id: id, platform })
      );
    } catch (err) {
      console.error('Failed to register device for push', err);
    }
  }

  /**
   * Unregister the current device. Called on logout.
   */
  async unregisterDevice() {
    const id = this.playerId();
    if (!id) return;
    try {
      await lastValueFrom(
        this.http.post('/auth/unregister-device', { player_id: id })
      );
    } catch (err) {
      console.error('Failed to unregister device', err);
    }
  }
}
